import NotFoundError from "~/libs/errors/NotFoundError";
import type {BaseApiFetchOptions} from "~/libs/interfaces/BaseApiFetchOptions";
import type SponsorLocal from "~/libs/interfaces/SponsorLocal";
import type SponsorFilters from "~/libs/interfaces/SponsorFilters";
import type SponsorStrapi from "~/libs/interfaces/SponsorStrapi";
import sponsorLocalFactory from "~/libs/factories/SponsorLocalFactory";

const sponsorStore = (type: string = 'sponsor', key?: string) => defineStore(`sponsor-${type}-${key}`, {
  state: () => {
    const state: {
      isLoading: Ref<boolean>
      limit: Ref<number>
      sponsors: SponsorLocal[]
      sponsor: Ref<SponsorLocal | null>
      filters: SponsorFilters
    } = {
      isLoading: ref(true),
      limit: ref(10),
      sponsors: reactive([]),
      sponsor: ref(null),
      filters: {},
    }
    return state
  },
  getters: {
    getIsLoading(state): boolean {
      return state.isLoading
    }
  },
  actions: {
    setLimit(limit: number) {
      this.limit = limit
    },
    /**
     * Get sponsor by id
     * @param id
     */
    getSponsor(id: string|number): SponsorLocal | undefined {
      const sponsor = this.sponsors.find((item) => {
        return String(item.id) === String(id)
      })
      return sponsor
    },
    /**
     * Fetch filter changed
     * @param filters
     */
    filterChanged(filters?: SponsorFilters): boolean {
      const sameArrays = (a?: string[], b?: string[]) => {
        if ((!a && b) || (a && !b)) {
          return false
        }
        if (!a && !b) {
          return true
        }
        if (a === b) {
          return true
        }
        if (a && b) {
          if (a.length !== b.length) {
            return false
          }
          const sortedA = a.sort()
          const sortedB = b.sort()

          for (let i = 0; i < sortedA.length; i++) {
            if (sortedA[i] !== sortedB[i]) {
              return false
            }
          }
        }
        return true
      }

      // check item ids filter
      if (!sameArrays(filters?.ids, this.filters.ids)) {
        return true
      }

      return false
    },

    checkFiltersChanges(filters: SponsorFilters): void {
      // reset items if filter changed
      if (this.filterChanged(filters)) {
        this.filters.ids = filters.ids
        this.sponsors = reactive([])
        this.sponsor = null
      }
    },

    /**
     * Fetch items from server
     * @return {Promise<void>}
     */
    async fetchSponsors(
      filters: SponsorFilters = {},
      options: BaseApiFetchOptions = {}
    ): Promise<void> {
      const logger = useFrontendLogger()
      const persistedStore = usePersistedStore()
      const { find } = useStrapi()

      const settings = {
        forceRefetch: false,
        checkFiltersChange: true,
        ...options
      }

      // reset items if filter changed
      if (settings.checkFiltersChange) {
        this.checkFiltersChanges(filters)
      }

      // no need to fetch items
      if (!settings.forceRefetch && this.sponsors.length >= this.limit && !persistedStore.needSync(this.$id)) {
        return
      }

      this.isLoading = true

      const apiFilters = {
        ...(filters.ids && {
          documentId: {
            $in: filters.ids,
          }
        }),
        isVisible: {
          $eq: true
        }
      }

      const params = {
        filters: apiFilters,
        populate: {
          image: true,
        },
        sort: "order",
        status: "published",
      }

      try {
        const result = await find<SponsorStrapi>('sponsors', params)
        //@ts-ignore
        const items = result.data || []
        this.processData(items, this.limit === 1)
        persistedStore.setLastSync(this.$id)
        this.isLoading = false
      } catch (e) {
        logger?.error('useSponsorsStore:fetchSponsors - exception', {
          errorDetail: e,
          strapiCallParams: params,
        })
        this.isLoading = false
        throw e
      }
    },
    /**
     * Process loaded data from Strapi
     * @param newItems
     * @param isDetail
     */
    processData(newItems: SponsorStrapi[], isDetail: boolean = false):void {
      if (newItems.length) {
        // check item detail load
        if (isDetail && newItems[0]) {
          this.setItem(sponsorLocalFactory.createSponsorFromStrapi(newItems[0]))
        } else if (isDetail && (!newItems || !newItems.length)) {
          throw new NotFoundError('Sponsor not found')
        }
        if (newItems && newItems.length) {
          // add new items to list
          this.addItems(newItems.map(sponsorLocalFactory.createSponsorFromStrapi).filter(item => !!item), true)
        }
      } else if (isDetail) {
        throw new NotFoundError('Sponsor not found')
      }
    },
    /**
     * Add items to store - remove duplicities
     * @param newItems
     * @param rewrite
     */
    addItems(newItems?: SponsorLocal[], rewrite: boolean = false): void {
      newItems?.forEach((newItem) => {
        const foundIndex = this.sponsors.findIndex(sponsor => sponsor.id === newItem.id)
        if (foundIndex >= 0 && rewrite) {
          this.sponsors.splice(foundIndex, 1)
        }
        if (foundIndex === -1 || rewrite) {
          this.sponsors.push(newItem)
        }
      })
    },

    setItem(item?: SponsorLocal): void {
      if (item) {
        this.sponsor = item
      } else {
        this.sponsor = null
      }
    }

  }
})

export const useSponsorsStore = (type?: string, key?: string) => {
  return sponsorStore(type, key)()
}
